import { useEffect, useRef, useState, useMemo, useCallback } from 'react';
import _ from 'lodash';
import { useQuery } from 'react-query';

import PropertyService from '../../../services/propertyService';
import {
  AGED_UP_MINOR_TYPE_ID,
  FINANCIAL_LEASE_SIGNER_TYPE_ID,
  NON_FINANCIAL_LEASE_SIGNER_TYPE_ID,
} from './constants';

export const useUpdateLeaseExpirationLimit = (
  isLeaseExpirationLimitsActive,
  organizationId,
  propertyId,
  toasterFn,
) => {
  const isMounted = useRef(true);
  useEffect(() => {
    return () => {
      isMounted.current = false;
    };
  }, []);

  const [isActive, setIsActive] = useState(isLeaseExpirationLimitsActive);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    safeSet(setIsActive)(isLeaseExpirationLimitsActive);
  }, [isLeaseExpirationLimitsActive]);

  const updateLeaseExpirationLimit = async (value) => {
    const propertyService = new PropertyService();

    try {
      safeSet(setIsLoading)(true);
      const response = await propertyService.updateLeaseExpirationLimit(
        organizationId,
        propertyId,
        value,
      );

      if (response) {
        safeSet(setIsActive)(response.isLeaseExpirationLimitsActive);
        toasterFn({
          message: `Lease expiration limits ${
            response.isLeaseExpirationLimitsActive ? 'activated' : 'deactivated'
          }.`,
          title: 'Success',
        });
      }
    } catch {
      toasterFn({
        type: 'error',
        message: 'Failed to update the lease expiration limits setting.',
        title: Error,
      });
    } finally {
      safeSet(setIsLoading)(false);
    }
  };

  const safeSet = (setFn: Function) => (value: string) => {
    if (isMounted.current) {
      setFn(value);
    }
  };

  return [isActive, isLoading, updateLeaseExpirationLimit];
};

export const useMasterMeteredUtilities = ({
  propertyDetails,
  onChange,
  masterMeteredUtilitiesOtherSelected,
  setMasterMeteredUtilitiesOtherSelected,
}) => {
  const [masterMeteredUtilities, setMasterMeteredUtilities] = useState({});

  useEffect(() => {
    const propertyConfigMMUtilities =
      propertyDetails.config?.masterMeteredUtilities || {};

    // Sort the keys of masterMeteredUtilities, ensuring 'other' is always last
    const mMUtilsKeys = Object.keys(propertyConfigMMUtilities);
    const mMUtilsKeysWithoutOtherSorted = mMUtilsKeys
      .filter((util) => util !== 'other')
      .sort();
    const otherMMUtilArr = mMUtilsKeys.filter((util) => util === 'other');
    const mMUtilsToSet = [...mMUtilsKeysWithoutOtherSorted, ...otherMMUtilArr];

    setMasterMeteredUtilities(
      mMUtilsToSet.reduce((acc, util) => {
        acc[util] = propertyConfigMMUtilities[util] || false;
        return acc;
      }, {}),
    );
    // Set the other utility text if it exists
    setOtherMMUtility(propertyConfigMMUtilities?.other || '');
  }, [propertyDetails.config?.masterMeteredUtilities]);

  const [otherMMUtility, setOtherMMUtility] = useState('');

  const mMUtilIsSelected = useCallback(
    (util: string) =>
      masterMeteredUtilities[util] ||
      (util === 'other' && masterMeteredUtilitiesOtherSelected),
    [masterMeteredUtilities, masterMeteredUtilitiesOtherSelected],
  );

  const noMMUtilsSelected = useMemo(() => {
    return Object.values(masterMeteredUtilities).every((value) => !value);
  }, [masterMeteredUtilities]);

  const masterMeteredUtilitiesText = useMemo(() => {
    // Sort text representation of master metered utilities
    const mMUtilsKeys = Object.keys(masterMeteredUtilities);
    const mMUtilsKeysWithoutOtherSorted = mMUtilsKeys
      .filter((util) => util !== 'other')
      .sort();
    const otherMMUtilArr = mMUtilsKeys.filter((util) => util === 'other');
    const mMTextToSet = [...mMUtilsKeysWithoutOtherSorted, ...otherMMUtilArr];
    return mMTextToSet
      .filter((util) => masterMeteredUtilities[util])
      .map((util) =>
        util === 'other'
          ? `${_.startCase(util)} - ${_.startCase(otherMMUtility)}`
          : _.startCase(util),
      )
      .join(', ');
  }, [masterMeteredUtilities, otherMMUtility]);

  const mMUtilOptions = useMemo(
    () =>
      Object.keys(masterMeteredUtilities).map((util) => ({
        value: util,
        text: _.startCase(util),
      })),
    [masterMeteredUtilities],
  );

  const currentMMUtils = useMemo(() => {
    return Object.keys(masterMeteredUtilities).filter((util) =>
      mMUtilIsSelected(util),
    );
  }, [masterMeteredUtilities, mMUtilIsSelected]);

  const onSelectedMMUtilitiesChange = useCallback(
    (selectedMMUtilities) => {
      setMasterMeteredUtilitiesOtherSelected(
        selectedMMUtilities.includes('other'),
      );
      onChange({
        target: {
          name: 'config.masterMeteredUtilities',
          value: Object.keys(masterMeteredUtilities).reduce((acc, util) => {
            const isSelected = selectedMMUtilities.includes(util);
            if (util === 'other') {
              acc[util] = isSelected ? otherMMUtility.trim() : '';
              return acc;
            }
            acc[util] = isSelected;
            return acc;
          }, {}),
        },
      });
    },
    [
      masterMeteredUtilities,
      onChange,
      otherMMUtility,
      setMasterMeteredUtilitiesOtherSelected,
    ],
  );
  const onOtherMMUtilityChange = useCallback(
    (event) => {
      const value = event.target.value;
      setOtherMMUtility(value);
      onChange({
        target: {
          name: 'config.masterMeteredUtilities.other',
          value: value,
        },
      });
    },
    [setOtherMMUtility, onChange],
  );
  return {
    otherMMUtility,
    mMUtilIsSelected,
    noMMUtilsSelected,
    masterMeteredUtilitiesText,
    mMUtilOptions,
    currentMMUtils,
    onSelectedMMUtilitiesChange,
    onOtherMMUtilityChange,
  };
};

export const useSecurityDepositInterest = ({
  onChange,
  organizationId,
  propertyId,
}) => {
  const securityDepositInterestPayoutFrequencyOptions = [
    'Monthly',
    'Quarterly',
    'Annually',
    'Move-Out',
  ].map((option) => ({
    value: option,
    text: option,
  }));

  const handleAccrueSecurityDepositInterestChange = (_, checked: boolean) => {
    if (!checked) {
      onChange({
        target: {
          name: 'config.securityDepositInterestPayoutFrequency',
          value: null,
        },
      });
    }
    onChange({
      target: {
        name: 'config.accrueSecurityDepositInterest',
        value: checked,
      },
    });
  };

  const handleSecurityDepositInterestPayoutFrequencyChange = (
    value: string,
  ) => {
    onChange({
      target: {
        name: 'config.securityDepositInterestPayoutFrequency',
        value,
      },
    });
  };

  const { propertyHasSDITransactionCodes, isLoading } =
    usePropertyHasSDITransactionCodes({
      organizationId,
      propertyId,
    });

  return {
    securityDepositInterestPayoutFrequencyOptions,
    handleAccrueSecurityDepositInterestChange,
    handleSecurityDepositInterestPayoutFrequencyChange,
    propertyHasSDITransactionCodes,
    isLoading,
  };
};

export const usePropertyHasSDITransactionCodes = ({
  organizationId,
  propertyId,
}) => {
  const [propertyHasSDITransactionCodes, setPropertyHasSDITransactionCodes] =
    useState(false);

  const { isLoading } = useQuery(
    ['propertySDITransactionCodes', organizationId, propertyId],
    () => {
      const propertyService = new PropertyService();
      return propertyService.getPropertySDITransactionCodes({
        organizationId,
        propertyId,
      });
    },
    {
      enabled: !!organizationId && !!propertyId,
      onSuccess: (data) => {
        if (data && data.length > 0) {
          setPropertyHasSDITransactionCodes(true);
        } else {
          setPropertyHasSDITransactionCodes(false);
        }
      },
      onError: () => {
        setPropertyHasSDITransactionCodes(false);
      },
    },
  );

  return { propertyHasSDITransactionCodes, isLoading };
};

export const useSubrentCodeCheck = ({
  organizationId,
  propertyId,
  feature = 'housing-assistance-vouchers',
}) => {
  const [isSubrentEnabled, setIsSubrentEnabled] = useState(true);

  const { isLoading } = useQuery(
    ['subrentCodeCheck', organizationId, propertyId, feature],
    () => {
      const propertyService = new PropertyService();
      return propertyService.checkCodesForFeature({
        organizationId,
        propertyId,
        feature,
      });
    },
    {
      enabled: !!organizationId && !!propertyId,
      onSuccess: (data) => {
        if (data && data.featureEnabled !== undefined) {
          setIsSubrentEnabled(data.featureEnabled);
        } else {
          // Default to enabled if response doesn't contain featureEnabled
          setIsSubrentEnabled(true);
        }
      },
      onError: () => {
        // Default to enabled on error
        setIsSubrentEnabled(true);
      },
    },
  );

  return { isSubrentEnabled, isLoading };
};
export const useAgedUpMinorType = ({ onChange }) => {
  const agedUpMinorTypeOptions = [
    {
      value: AGED_UP_MINOR_TYPE_ID,
      text: 'Aged Up Minor',
    },
    {
      value: FINANCIAL_LEASE_SIGNER_TYPE_ID,
      text: 'Financial Lease Signer',
    },
    {
      value: NON_FINANCIAL_LEASE_SIGNER_TYPE_ID,
      text: 'Non-Financial Lease Signer',
    },
  ];

  const handleAgedUpMinorTypeChange = (value: string) => {
    onChange({
      target: {
        name: 'config.agedUpMinorType',
        value,
      },
    });
  };

  return {
    agedUpMinorTypeOptions,
    handleAgedUpMinorTypeChange,
  };
};

export const useLastSprinklerDate = ({ propertyDetails, onChange }) => {
  return useMemo(() => {
    const lastSprinklerDateValue = _.get(
      propertyDetails,
      'config.sprinklerInspectionDate',
      null,
    );

    const lastSprinklerDateOnChange = (date: Object) => {
      onChange({
        target: {
          name: 'config.sprinklerInspectionDate',
          value: date,
        },
      });
    };

    return {
      lastSprinklerDateValue,
      lastSprinklerDateOnChange,
    };
  }, [propertyDetails.config.sprinklerInspectionDate]);
};
